'use client';

import { useEffect } from 'react';
import {
	USDT_SPOT_MARKET_INDEX,
	SOL_SPOT_MARKET_INDEX,
} from '@velocity-exchange/common';
import type { UISerializableDepositRecord } from '@velocity-exchange/common';
import type { VersionedTransactionResponse } from '@solana/web3.js';
import useAppEventEmitter from './useAppEventEmitter';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { notify } from 'src/utils/notifications';
import useModal from 'src/components/Modals/hooks/useModal';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

const FUN_XYZ_SIGNER_WHITELIST = new Set([
	'zApVWDs3nSychNnUXSS2czhY78Ycopa15zELrK2gAdM',
	'4B62MS5gxpRZ2hwkGCNAAayA5f7LYZRW4z1ASSfU3SXo',
]);

const FUN_XYZ_TARGET_MARKETS = new Set([
	USDT_SPOT_MARKET_INDEX,
	SOL_SPOT_MARKET_INDEX,
]);

const MARKET_TO_ASSET: Record<number, string> = {
	[USDT_SPOT_MARKET_INDEX]: 'USDC',
	[SOL_SPOT_MARKET_INDEX]: 'SOL',
};

function extractAccountPubkeys(
	res: VersionedTransactionResponse | null
): string[] {
	if (!res?.transaction?.message) return [];
	const { transaction, meta } = res;
	const accountKeys = transaction.message.getAccountKeys(
		meta?.loadedAddresses
			? { accountKeysFromLookups: meta.loadedAddresses }
			: undefined
	);
	return accountKeys
		.keySegments()
		.flat()
		.map((pk) => pk.toBase58());
}

/**
 * Passively listens for Fun.xyz deposits (USDC or SOL). Shows a success notification
 * when a deposit with a Fun.xyz signer is detected.
 */
export default function useFunXyzDepositWatcher() {
	const appEventEmitter = useAppEventEmitter();
	const connection = useVelocityStore((s) => s.connection.current);

	const { currentModal, hide } = useModal();
	const currentModalTypeRef = useSyncedRef(currentModal?.type);
	const hideRef = useSyncedRef(hide);

	useEffect(() => {
		if (!connection) return;

		const handler = async (payload?: {
			depositRecord: UISerializableDepositRecord;
		}) => {
			if (!payload?.depositRecord) return;

			const { depositRecord } = payload;
			if (!FUN_XYZ_TARGET_MARKETS.has(depositRecord.marketIndex)) return;

			try {
				const res = await connection.getTransaction(depositRecord.txSig, {
					maxSupportedTransactionVersion: 1,
					commitment: 'finalized',
				});
				const accountPubkeys = extractAccountPubkeys(res);
				const hasFunXyzSigner = accountPubkeys.some((pk) =>
					FUN_XYZ_SIGNER_WHITELIST.has(pk)
				);
				if (hasFunXyzSigner) {
					const asset = MARKET_TO_ASSET[depositRecord.marketIndex] ?? 'deposit';

					notify({
						type: 'success',
						message: `Your ${asset} deposit has been received`,
					});

					// Auto-close the modal if it's still open.
					if (currentModalTypeRef.current === 'deposit_from_other_sources') {
						hideRef.current();
					}
				}
			} catch {
				// Silently ignore fetch errors; user can check balance
			}
		};

		appEventEmitter.on('newDepositRecord', handler);
		return () => {
			appEventEmitter.removeListener('newDepositRecord', handler);
		};
	}, [appEventEmitter, connection]);
}
